import Button from '@/components/UI/Button';
import { errorToast, successToast } from '@/helpers';
import { _isNotBlank } from '@/littledash';
import InVivoError from '@/model/InVivoError';
import type { UserGroupMembersUpdatePayload, UserGroupRow, UserGroupUpdatePayload } from '@/model/UserGroups.model';
import { ApiService, ResponseType } from '@/support/ApiService';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import type { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { fieldValidationMessage } from './UserGroupErrors';
import UserGroupFields, { type UserGroupFormValues } from './UserGroupFields';
import { useEligibleMembers } from './useEligibleMembers';

interface EditUserGroupModalProps {
  closeModal: () => void;
  group: UserGroupRow;
  onUpdated: (group: UserGroupRow) => void;
}

/**
 * Edits a group's name, description and members.
 *
 * Saving is two requests because the details and the member list are separate resources. Details go
 * first: if membership were written first and the rename then failed, the administrator would be
 * looking at a dialog reporting failure over a membership change that had already taken effect.
 */
const EditUserGroupModal: FC<EditUserGroupModalProps> = ({ closeModal, group, onUpdated }) => {
  const formMethods = useForm<UserGroupFormValues>({
    defaultValues: {
      name: group.name,
      description: group.description ?? '',
      memberApiIds: group.member_api_ids,
    },
  });
  const members = useEligibleMembers();
  const {
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = formMethods;

  const onSubmit = async ({ name, description, memberApiIds }: UserGroupFormValues) => {
    const detailsBody: UserGroupUpdatePayload = {
      name: name.trim(),
      description: _isNotBlank(description) ? description.trim() : null,
    };
    const detailsResponse = await ApiService.call({
      endpoint: 'PATCH /api/v1/user-groups/{userGroupId}',
      path: { userGroupId: group.api_id },
      body: detailsBody,
      options: { onError: { toast: false, capture: false, throw: false } },
    });

    if (detailsResponse.type === ResponseType.Error) {
      const nameMessage = fieldValidationMessage(detailsResponse, 'name');
      if (_isNotBlank(nameMessage)) {
        setError('name', { type: 'server', message: nameMessage });
      } else {
        errorToast('Could not save the group');
        ExceptionHandler.captureException(
          new InVivoError('Could not update user group', {
            cause: detailsResponse.error,
            slug: 'user-group-update',
          })
        );
      }
      return;
    }

    const membersBody: UserGroupMembersUpdatePayload = { member_api_ids: memberApiIds };
    const membersResponse = await ApiService.call({
      endpoint: 'POST /api/v1/user-groups/{userGroupId}/members',
      path: { userGroupId: group.api_id },
      body: membersBody,
      options: { onError: { toast: false, capture: false, throw: false } },
    });

    if (membersResponse.type === ResponseType.Error) {
      const memberMessage = fieldValidationMessage(membersResponse, 'member_api_ids');
      errorToast(_isNotBlank(memberMessage) ? memberMessage : 'Saved the group, but not its members');
      ExceptionHandler.captureException(
        new InVivoError('Could not update user group members', {
          cause: membersResponse.error,
          slug: 'user-group-members-update',
        })
      );
      onUpdated(detailsResponse.body);
      return;
    }

    const updated = membersResponse.body;
    successToast(`Saved group ${updated.name}`);
    onUpdated(updated);
    closeModal();
  };

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="center mw7 ui-card"
        data-test-component="EditUserGroupModal"
        data-test-element="container"
      >
        <div className="pa4" data-test-component="EditUserGroupModal" data-test-element="header">
          <h3 className="normal f4" data-testid="title">
            Edit user group
          </h3>
        </div>
        <div data-test-component="EditUserGroupModal" data-test-element="body">
          <UserGroupFields submitting={isSubmitting} members={members} />
        </div>
        <div
          className="bt b--moon-gray bg-white pa3"
          data-test-component="EditUserGroupModal"
          data-test-element="footer"
        >
          <Button submit className="mr2" disabled={isSubmitting || members.failed} testId="submit-button">
            {isSubmitting ? 'Saving…' : 'Save changes'}
          </Button>
          <Button plain onClick={closeModal} disabled={isSubmitting} testId="cancel-button">
            Cancel
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default EditUserGroupModal;
